import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { Edit, Person, School as SchoolIcon, Sports } from '@mui/icons-material'
import { DIVISION_LABELS, TRACK_EVENTS } from '@tnf-recruit/shared'
import { useRecruiterProfile } from '../../../hooks/useRecruiterProfile'
import { ProfileSection } from '../ProfileSection'
import { ProfileField } from '../ProfileField'
import { EmptyStatePanel } from '../../StatePanel'

const getEventName = (eventId: string) => TRACK_EVENTS.find(e => e.id === eventId)?.name ?? eventId

/** Read-only view of the recruiter's own profile — mirrors what a student sees on the school page. */
export const RecruiterProfileView = () => {
  const navigate = useNavigate()
  const { profile } = useRecruiterProfile()

  if (profile.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (!profile.data) {
    return (
      <EmptyStatePanel
        title="No profile yet"
        description="Complete onboarding to create your recruiter profile."
      />
    )
  }

  const p = profile.data
  const school = p.school
  const ar = p.recruitingFocus.academicRequirements

  return (
    <Box>
      <ProfileSection
        title={`${p.contactInfo.firstName} ${p.contactInfo.lastName}`}
        icon={
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
            <Person />
          </Avatar>
        }
        action={
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate('/profile/edit')}
          >
            Edit profile
          </Button>
        }
      >
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {p.position}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {school?.name && <Chip label={school.name} color="primary" />}
          {school?.division && (
            <Chip
              label={
                DIVISION_LABELS[school.division as keyof typeof DIVISION_LABELS] ?? school.division
              }
            />
          )}
          {school?.city && school?.state && <Chip label={`${school.city}, ${school.state}`} />}
        </Box>
      </ProfileSection>

      <Alert severity="info" sx={{ my: 3 }}>
        Athletes browsing schools see your position, email, phone, and bio on your school's page.
      </Alert>

      <Stack spacing={3}>
        <ProfileSection title="Contact" icon={<Person />}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Email" value={p.contactInfo.email} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Phone" value={p.contactInfo.phone} />
            </Grid>
            {p.bio && (
              <Grid item xs={12}>
                <ProfileField label="Bio" value={p.bio} />
              </Grid>
            )}
          </Grid>
        </ProfileSection>

        <ProfileSection title="College information" icon={<SchoolIcon />}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ProfileField label="School" value={school?.name} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="Division"
                value={
                  school?.division
                    ? (DIVISION_LABELS[school.division as keyof typeof DIVISION_LABELS] ??
                      school.division)
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Conference" value={school?.conference} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="Location"
                value={
                  school?.city && school?.state ? `${school.city}, ${school.state}` : undefined
                }
              />
            </Grid>
            <Grid item xs={12}>
              <ProfileField
                label="Website"
                value={
                  school?.website ? (
                    <a href={school.website} target="_blank" rel="noopener noreferrer">
                      {school.website}
                    </a>
                  ) : undefined
                }
              />
            </Grid>
          </Grid>
        </ProfileSection>

        <ProfileSection title="Recruiting focus" icon={<Sports />}>
          <Typography variant="subtitle2" gutterBottom>
            Events
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {p.recruitingFocus.events.length > 0 ? (
              p.recruitingFocus.events.map(eventId => (
                <Chip key={eventId} label={getEventName(eventId)} color="primary" size="small" />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No events selected.
              </Typography>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <ProfileField label="Minimum GPA" value={ar.minGPA ?? 'No minimum'} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ProfileField label="Minimum SAT" value={ar.minSAT ?? 'No minimum'} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ProfileField label="Minimum ACT" value={ar.minACT ?? 'No minimum'} />
            </Grid>
          </Grid>
        </ProfileSection>
      </Stack>
    </Box>
  )
}
